"use client";
import { IconCryptoWallet } from "@central-icons-react/square-outlined-radius-0-stroke-1.5";

import {
  IconArrowDownToLineOutline18,
  IconArrowUpRightOutline18,
  IconCheckOutline18,
  IconCircleInfoOutline18,
  IconCopyOutline18,
  IconDownload2Outline18,
} from "nucleo-ui-outline-18";

import type { TurnkeyWallet } from "@/app/(v2)/_types/wallet";
import type { RequiredToken } from "@/app/(v2)/workflows/_components/form-fields/RequiredTokensContext";
import ResponsiveModal from "@/components/custom/ResponsiveModal";
import { WalletAddressMeta } from "@/components/ui/WalletAddressMeta";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import { Button } from "@/components/ui/button";
import { TokenIcon } from "@/components/ui/token-icon";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { useSimBalance, type SimBalanceItem } from "@/hooks/useSimTokenInfo";
import { addressesMatch } from "@/lib/tokens/nativeToken";
import { showToast } from "@/lib/toast";
import { PixelLoader } from "@/components/ui/pixel-loader";
import { cn } from "@/lib/utils";
import { useModalStore } from "@b3dotfun/sdk/global-account/react";
import Link from "next/link";
import { useMemo, useState } from "react";
import { useChainInfoMap } from "../workflows/_hooks/useChainInfo";
import { SearchableWalletSelect } from "./SearchableWalletSelect";

interface WalletListSelectorProps {
  wallets: TurnkeyWallet[];
  selectedAddress?: string;
  onSelect: (address: string) => void;
  isLoading?: boolean;
  variant?: "default" | "compact";
  showCreateLink?: boolean;
  emptyStateMessage?: string;
  requiredTokens?: RequiredToken[];
  /** When provided, renders an "Import Wallet" button at the bottom of the wallet list. */
  onImportWallet?: () => void;
}

interface WalletItemProps {
  wallet: TurnkeyWallet;
  index: number;
  isSelected: boolean;
  onSelect: () => void;
  variant: "default" | "compact";
  requiredTokens?: RequiredToken[];
}

const SEARCHABLE_WALLET_THRESHOLD = 5;

interface WalletOptionContentProps {
  wallet: TurnkeyWallet;
  index: number;
  isSelected: boolean;
  variant: "default" | "compact";
  requiredTokens?: RequiredToken[];
  showInfoButton?: boolean;
  copyButtonSize?: "default" | "small";
}

/**
 * Individual wallet item with balance fetching
 */
export function WalletOptionContent({
  wallet,
  index,
  isSelected,
  variant,
  requiredTokens,
  showInfoButton = true,
  copyButtonSize = "default",
}: WalletOptionContentProps) {
  const [infoModalOpen, setInfoModalOpen] = useState(false);
  const [copiedAddress, setCopiedAddress] = useState(false);

  // AnySpend modal for deposits
  const setModalOpen = useModalStore(state => state.setB3ModalOpen);
  const setModalContentType = useModalStore(state => state.setB3ModalContentType);

  // Fetch balance for this wallet
  const { data: balanceData, isLoading: isLoadingBalance, refetch: refetchBalance } = useSimBalance(wallet.address);

  // Calculate total USD value and get unique chains with balances
  const { totalUsd, chainsWithBalances } = useMemo(() => {
    if (!balanceData?.balances) return { totalUsd: 0, chainsWithBalances: [] as number[] };

    let total = 0;
    const chainSet = new Set<number>();

    balanceData.balances.forEach((b: SimBalanceItem) => {
      if (b.value_usd && b.value_usd > 0) {
        total += b.value_usd;
        chainSet.add(b.chain_id);
      }
    });

    return { totalUsd: total, chainsWithBalances: Array.from(chainSet) };
  }, [balanceData]);

  // Fetch chain info for chains with balances (max 3)
  const topChains = chainsWithBalances.slice(0, 3);
  const { chainInfoMap, isLoading: isLoadingChains } = useChainInfoMap(topChains);
  const chainBadges = useMemo(
    () =>
      topChains.map(chainId => {
        const chainInfo = chainInfoMap.get(chainId);
        return {
          id: chainId,
          name: chainInfo?.name || `Chain ${chainId}`,
          iconUrl: chainInfo?.iconUrl,
        };
      }),
    [chainInfoMap, topChains],
  );

  // Get all chain IDs for the modal (include requiredTokens chains for filtering)
  const allChainIds = useMemo(() => {
    const ids = new Set<number>();
    if (balanceData?.balances) {
      for (const b of balanceData.balances) ids.add(b.chain_id);
    }
    if (requiredTokens) {
      for (const rt of requiredTokens) ids.add(rt.chainId);
    }
    return [...ids];
  }, [balanceData, requiredTokens]);
  const { chainInfoMap: allChainInfoMap } = useChainInfoMap(allChainIds);

  // Format USD value
  const formattedUsd = useMemo(() => {
    if (totalUsd >= 1000000) {
      return `$${(totalUsd / 1000000).toFixed(2)}M`;
    }
    if (totalUsd >= 1000) {
      return `$${(totalUsd / 1000).toFixed(2)}K`;
    }
    if (totalUsd >= 1) {
      return `$${totalUsd.toFixed(2)}`;
    }
    if (totalUsd > 0) {
      return `$${totalUsd.toFixed(4)}`;
    }
    return "$0.00";
  }, [totalUsd]);

  // Group balances by chain for the modal
  const balancesByChain = useMemo(() => {
    if (!balanceData?.balances) return new Map<number, SimBalanceItem[]>();

    const grouped = new Map<number, SimBalanceItem[]>();
    balanceData.balances.forEach((b: SimBalanceItem) => {
      const existing = grouped.get(b.chain_id) || [];
      existing.push(b);
      grouped.set(b.chain_id, existing);
    });
    return grouped;
  }, [balanceData]);

  // When requiredTokens is provided, filter balances to only show matching tokens
  // and add placeholder entries for tokens the wallet doesn't hold yet
  const filteredBalancesByChain = useMemo(() => {
    if (!requiredTokens || requiredTokens.length === 0) return balancesByChain;

    const filtered = new Map<number, SimBalanceItem[]>();

    for (const rt of requiredTokens) {
      const chainBalances = balancesByChain.get(rt.chainId) || [];
      const match = chainBalances.find(b => addressesMatch(b.address, rt.address));

      const existing = filtered.get(rt.chainId) || [];
      if (match) {
        existing.push(match);
      } else {
        // Create a placeholder for missing required tokens
        existing.push({
          chain: "",
          chain_id: rt.chainId,
          address: rt.address,
          amount: "0",
          symbol: rt.symbol,
          decimals: 18,
          price_usd: 0,
          value_usd: 0,
        });
      }
      filtered.set(rt.chainId, existing);
    }

    return filtered;
  }, [balancesByChain, requiredTokens]);

  const handleInfoClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    setInfoModalOpen(true);
  };

  const copyAddress = async (e: React.SyntheticEvent) => {
    e.stopPropagation();
    e.preventDefault();
    try {
      await navigator.clipboard.writeText(wallet.address);
      setCopiedAddress(true);
      showToast.success("Address copied");
      setTimeout(() => setCopiedAddress(false), 2000);
    } catch {
      showToast.error("Failed to copy address");
    }
  };

  const handleDeposit = (e: React.MouseEvent) => {
    e.stopPropagation();
    e.preventDefault();

    setModalContentType({
      type: "anySpendDeposit",
      recipientAddress: wallet.address,
      destinationTokenAddress: "0x0000000000000000000000000000000000000000",
      destinationTokenChainId: 8453, // Base as default
      actionLabel: "Deposit to wallet",
      onSuccess: async () => {
        showToast.success("Deposit successful!");
        refetchBalance();
      },
      allowDirectTransfer: true,
      showFiatOption: false,
    });
    setModalOpen(true);
  };

  // Calculate human-readable balance
  const formatHumanBalance = (balance: SimBalanceItem) => {
    const balanceBigInt = BigInt(balance.amount);
    const divisor = BigInt(10) ** BigInt(balance.decimals);
    const wholePart = balanceBigInt / divisor;
    const remainder = balanceBigInt % divisor;
    const decimalPart = remainder.toString().padStart(balance.decimals, "0").replace(/0+$/, "");
    return decimalPart ? `${wholePart}.${decimalPart}` : wholePart.toString();
  };

  return (
    <>
      <div className="flex items-center gap-3">
        {variant === "default" && (
          <div className="bg-ds-surface-card flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-full">
            {isSelected ? (
              <IconCheckOutline18 className="h-5 w-5 text-blue-600 dark:text-blue-400" />
            ) : (
              <IconCryptoWallet className="text-ds-text-subtle dark:text-ds-text-disabled h-5 w-5" />
            )}
          </div>
        )}
        {variant === "compact" && isSelected && (
          <IconCheckOutline18 className="h-4 w-4 flex-shrink-0 text-blue-600 dark:text-blue-400" />
        )}
        <div className="min-w-0 flex-1">
          <div className="flex items-center justify-between gap-2">
            <span className="flex items-center gap-1.5">
              <span className={cn("text-ds-text-primary font-medium", variant === "compact" && "text-sm")}>
                {wallet.walletName || `Wallet ${index + 1}`}
              </span>
              {wallet.accountType === "smart_wallet" && (
                <Badge variant="success" className="micro-label px-1.5 py-0">
                  Smart
                </Badge>
              )}
              {wallet.isImported && (
                <Badge variant="purple" className="micro-label px-1.5 py-0">
                  Imported
                </Badge>
              )}
            </span>
            {showInfoButton && (
              <TooltipProvider>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <span
                      role="button"
                      tabIndex={0}
                      onClick={handleInfoClick}
                      onKeyDown={e => {
                        if (e.key === "Enter" || e.key === " ") {
                          e.stopPropagation();
                          setInfoModalOpen(true);
                        }
                      }}
                      className="text-ds-text-disabled hover:bg-ds-surface-hover hover:text-ds-text-subtle dark:hover:bg-ds-neutral-800 flex-shrink-0 cursor-pointer rounded p-1 transition-colors dark:hover:text-zinc-300"
                    >
                      <IconCircleInfoOutline18 className="h-3.5 w-3.5" />
                    </span>
                  </TooltipTrigger>
                  <TooltipContent>View wallet details</TooltipContent>
                </Tooltip>
              </TooltipProvider>
            )}
          </div>
          <div className="flex items-center gap-1">
            <WalletAddressMeta
              address={wallet.address}
              usdValue={!isLoadingBalance && totalUsd > 0 ? formattedUsd : null}
              chains={!isLoadingChains ? chainBadges : []}
              extraChainsCount={chainsWithBalances.length > 3 ? chainsWithBalances.length - 3 : 0}
              isLoadingBalance={isLoadingBalance}
            />
            <span
              role="button"
              tabIndex={0}
              onClick={copyAddress}
              onKeyDown={e => {
                if (e.key === "Enter" || e.key === " ") copyAddress(e);
              }}
              aria-label={copiedAddress ? "Copied" : "Copy address"}
              className={cn(
                "text-ds-text-disabled hover:text-ds-text-subtle inline-flex cursor-pointer rounded transition-colors dark:hover:text-zinc-300",
                copyButtonSize === "small" ? "p-0" : "p-0.5",
              )}
            >
              {copiedAddress ? (
                <IconCheckOutline18
                  className={cn("text-green-600", copyButtonSize === "small" ? "h-2.5 w-2.5" : "h-3 w-3")}
                />
              ) : (
                <IconCopyOutline18 className={cn(copyButtonSize === "small" ? "h-2.5 w-2.5" : "h-3 w-3")} />
              )}
            </span>
          </div>
        </div>
      </div>
      {/* Wallet Info Modal */}
      <ResponsiveModal open={infoModalOpen} onOpenChange={setInfoModalOpen} title="Wallet Details">
        <div className="space-y-4">
          {/* Header */}
          <div className="flex items-center gap-3">
            <div className="bg-ds-neutral-100 dark:bg-ds-neutral-800 flex h-12 w-12 items-center justify-center rounded-full">
              <IconCryptoWallet className="text-ds-text-subtle dark:text-ds-text-disabled h-6 w-6" />
            </div>
            <div className="min-w-0 flex-1">
              <h3 className="text-ds-text-primary text-lg font-semibold">
                {wallet.walletName || `Wallet ${index + 1}`}
              </h3>
              <div className="flex items-center gap-2">
                <p className="text-ds-text-muted font-mono text-sm">
                  {wallet.address.slice(0, 10)}...{wallet.address.slice(-8)}
                </p>
                <button
                  onClick={copyAddress}
                  className="text-ds-text-disabled hover:bg-ds-surface-hover hover:text-ds-text-subtle dark:hover:bg-ds-neutral-800 rounded p-1 transition-colors dark:hover:text-zinc-300"
                >
                  {copiedAddress ? (
                    <IconCheckOutline18 className="h-3.5 w-3.5 text-green-600" />
                  ) : (
                    <IconCopyOutline18 className="h-3.5 w-3.5" />
                  )}
                </button>
              </div>
            </div>
          </div>

          {/* Total Balance + Deposit Button */}
          <div className="border-ds-border-default flex items-center justify-between rounded-lg border bg-zinc-50 p-4 dark:bg-zinc-800/50">
            <div>
              <p className="label label-xs">Total Balance</p>
              <p className="text-ds-text-primary mt-1 text-2xl font-bold">
                {isLoadingBalance ? (
                  <span className="text-ds-text-disabled flex items-center gap-2 text-base">
                    <PixelLoader size="sm" /> Loading...
                  </span>
                ) : (
                  formattedUsd
                )}
              </p>
            </div>
            <Button size="sm" onClick={handleDeposit}>
              <span className="flex items-center gap-1.5 px-2 text-xs">
                <IconArrowDownToLineOutline18 className="h-3.5 w-3.5" />
                Deposit
              </span>
            </Button>
          </div>

          {/* Token Balances */}
          {!isLoadingBalance && filteredBalancesByChain.size > 0 && (
            <div className="space-y-3">
              <h4 className="label label-xs">
                {requiredTokens && requiredTokens.length > 0 ? "Required Tokens" : "Token Balances"}
              </h4>
              <div className="divide-ds-neutral-200 border-ds-border-default dark:divide-ds-neutral-700 max-h-64 divide-y overflow-y-auto rounded-lg border">
                {Array.from(filteredBalancesByChain.entries()).flatMap(([chainId, balances]) => {
                  const chainInfo = allChainInfoMap.get(chainId);

                  return balances.map((balance, idx) => {
                    const humanBalance = formatHumanBalance(balance);
                    // For native token placeholders (0x000...000), use chain icon
                    const isNativeToken = /^0x0+$/.test(balance.address);
                    const logoUrl = balance.token_metadata?.logo || (isNativeToken ? chainInfo?.iconUrl : undefined);

                    return (
                      <div
                        key={`${chainId}-${balance.address}-${idx}`}
                        className="flex items-center justify-between p-3"
                      >
                        <div className="flex items-center gap-3">
                          <TokenIcon
                            logoUrl={logoUrl}
                            chainId={balance.chain_id}
                            tokenAddress={balance.address}
                            symbol={balance.symbol}
                            size={32}
                          />
                          <div>
                            <div className="flex items-center gap-2">
                              <span className="body-sm-semibold">{balance.symbol}</span>
                              {chainInfo && <span className="label-xs">on {chainInfo.name}</span>}
                            </div>
                            <div className="text-ds-text-subtle dark:text-ds-text-disabled text-xs">
                              {parseFloat(humanBalance).toLocaleString(undefined, { maximumFractionDigits: 6 })}{" "}
                              {balance.symbol}
                            </div>
                          </div>
                        </div>
                        <div className="text-right">
                          {balance.value_usd && balance.value_usd > 0 ? (
                            <span className="text-ds-text-secondary text-sm font-medium">
                              $
                              {balance.value_usd.toLocaleString(undefined, {
                                minimumFractionDigits: 2,
                                maximumFractionDigits: 2,
                              })}
                            </span>
                          ) : (
                            requiredTokens &&
                            requiredTokens.length > 0 && (
                              <span className="text-ds-text-disabled dark:text-ds-text-muted text-sm font-medium">
                                $0.00
                              </span>
                            )
                          )}
                        </div>
                      </div>
                    );
                  });
                })}
              </div>
            </div>
          )}

          {/* Empty state — only show when no requiredTokens filter and no balances */}
          {!isLoadingBalance && filteredBalancesByChain.size === 0 && (
            <div className="border-ds-neutral-300 dark:border-ds-neutral-700 rounded-lg border border-dashed p-6 text-center">
              <p className="text-ds-text-muted text-sm">No token balances found</p>
              <Button variant="black" size="sm" onClick={handleDeposit} className="mt-3">
                <span className="flex items-center gap-1.5 px-2 text-xs">
                  <IconArrowDownToLineOutline18 className="h-3.5 w-3.5" />
                  Deposit Funds
                </span>
              </Button>
            </div>
          )}

          {/* Wallet Management Link */}
          <Button variant="white" asChild className="w-full">
            <Link
              href="/wallet-management"
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center justify-center gap-2"
            >
              <IconArrowUpRightOutline18 className="h-4 w-4" />
              Manage Wallets
            </Link>
          </Button>
        </div>
      </ResponsiveModal>
    </>
  );
}

function ImportWalletButton({ onClick, variant }: { onClick: () => void; variant: "default" | "compact" }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={cn(
        "border-ds-border-default hover:border-ds-neutral-300 w-full rounded-lg border border-dashed text-left transition-all hover:shadow-sm dark:hover:border-zinc-600",
        variant === "compact" ? "p-2.5" : "p-3",
      )}
    >
      <div className="flex items-center gap-3">
        {variant === "default" && (
          <div className="bg-ds-surface-card flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-full">
            <IconDownload2Outline18 className="text-ds-text-subtle dark:text-ds-text-disabled h-5 w-5" />
          </div>
        )}
        <div className="min-w-0 flex-1">
          <span className={cn("text-ds-text-primary font-medium", variant === "compact" && "text-sm")}>
            Import Wallet
          </span>
          <p className="text-ds-text-subtle dark:text-ds-text-disabled text-xs">
            Bring an existing private key into your organization
          </p>
        </div>
      </div>
    </button>
  );
}

function WalletItem({ wallet, index, isSelected, onSelect, variant, requiredTokens }: WalletItemProps) {
  return (
    <button
      onClick={onSelect}
      className={cn(
        "w-full rounded-lg border text-left transition-all",
        variant === "compact" ? "p-2.5" : "p-3",
        isSelected
          ? "shadow-ds-soft border-blue-500 bg-blue-50 dark:border-blue-500 dark:bg-blue-500/10"
          : "border-ds-border-default hover:border-ds-neutral-300 hover:shadow-sm dark:hover:border-zinc-600",
        variant === "compact" ? "" : "bg-ds-surface-card",
      )}
    >
      <WalletOptionContent
        wallet={wallet}
        index={index}
        isSelected={isSelected}
        variant={variant}
        requiredTokens={requiredTokens}
      />
    </button>
  );
}

/**
 * Reusable wallet list selector component
 * Used across WalletSelector, TransferModal, and other wallet selection UIs
 */
export function WalletListSelector({
  wallets,
  selectedAddress,
  onSelect,
  isLoading = false,
  variant = "default",
  showCreateLink = true,
  emptyStateMessage = "No workflow wallets found.",
  requiredTokens,
  onImportWallet,
}: WalletListSelectorProps) {
  // Loading state — render skeleton rows matching the wallet item shape
  if (isLoading) {
    if (variant === "compact") {
      return (
        <div className="flex items-center justify-center px-3 py-4">
          <PixelLoader size="md" color="blue" />
        </div>
      );
    }
    return (
      <div className="animate-in fade-in space-y-2 duration-300">
        {Array.from({ length: 3 }).map((_, i) => (
          <div
            key={i}
            className="border-ds-border-default bg-ds-surface-card flex items-center gap-3 rounded-lg border p-3"
          >
            <Skeleton className="h-8 w-8 rounded-full" />
            <div className="flex-1 space-y-1.5">
              <Skeleton className="h-4 w-32" />
              <Skeleton className="h-3 w-48" />
            </div>
            <Skeleton className="h-5 w-16 rounded-full" />
          </div>
        ))}
      </div>
    );
  }

  // Empty state
  if (wallets.length === 0) {
    return (
      <div
        className={`border-ds-neutral-200 rounded-lg border bg-zinc-50 dark:border-zinc-700 dark:bg-zinc-800/50 ${
          variant === "compact" ? "px-3 py-3" : "p-4"
        }`}
      >
        <p className="body-sm">
          {emptyStateMessage}{" "}
          {showCreateLink && (
            <Link href="/wallet-management" className="text-blue-600 underline hover:text-blue-700 dark:text-blue-400">
              Create one
            </Link>
          )}
        </p>
      </div>
    );
  }

  if (wallets.length > SEARCHABLE_WALLET_THRESHOLD) {
    return (
      <SearchableWalletSelect
        wallets={wallets}
        selectedAddress={selectedAddress}
        onSelect={onSelect}
        variant={variant}
        onImportWallet={onImportWallet}
      />
    );
  }

  // Wallet list
  return (
    <div className={variant === "compact" ? "space-y-1.5" : "space-y-2"}>
      {wallets.map((wallet, index) => {
        const isSelected = selectedAddress?.toLowerCase() === wallet.address.toLowerCase();

        return (
          <WalletItem
            key={wallet.address}
            wallet={wallet}
            index={index}
            isSelected={isSelected}
            onSelect={() => onSelect(wallet.address)}
            variant={variant}
            requiredTokens={requiredTokens}
          />
        );
      })}
      {onImportWallet && <ImportWalletButton onClick={onImportWallet} variant={variant} />}
    </div>
  );
}
